import {
	usePostFertilizer,
	useUpdateAvatarsFetilizer,
	useUpdateFertilizer
} from '@practium/features/api';
import { useToast } from '@practium/ui';
import { useRef, useState } from 'react';
import { DEFAULT_FERTILIZER_FORM } from '../constants/fertilizer.constants';
import { FertilizerSchema } from '../schemas/fertilizer.schema';
import type { FertilizerFormData, Manufacturer } from '../types';

const FIELD_LABELS: Record<string, string> = {
	name: 'Identificação',
	manufacturerId: 'Fabricante',
	category: 'Categoria',
	applicationMethods: 'Método Aplicação',
	nature: 'Natureza do Produto',
	active: 'Status',
	N: 'Nitrogênio (N)',
	P2O5: 'Fósforo (P₂O₅)',
	K2O: 'Potássio (K₂O)',
	Ca: 'Cálcio (Ca)',
	Mg: 'Magnésio (Mg)',
	S: 'Enxofre (S)',
	C: 'Carbono (C)',
	B: 'Boro (B)',
	Cu: 'Cobre (Cu)',
	Fe: 'Ferro (Fe)',
	Mn: 'Manganês (Mn)',
	Zn: 'Zinco (Zn)',
	Mo: 'Molibdênio (Mo)',
	CaO: 'Óxido de Cálcio (CaO)',
	MgO: 'Óxido de Magnésio (MgO)',
	PRNT: 'PRNT',
};

export interface UseFertilizerFormOptions {
	manufacturers: Manufacturer[];
	initialData?: FertilizerFormData;
	onSubmit: () => void;
	id?: string;
	file?: File[];
}

const parseNutrient = (value: string): number | undefined =>
	value !== '' && !Number.isNaN(Number(value)) ? Number(value) : undefined;

export const useFertilizerForm = ({
	manufacturers,
	initialData,
	onSubmit,
	id,
	file = [],
}: UseFertilizerFormOptions) => {
	const toast = useToast();
	const initialForm = initialData
		? { ...DEFAULT_FERTILIZER_FORM, ...initialData }
		: { ...DEFAULT_FERTILIZER_FORM };
	const [form, setForm] = useState<FertilizerFormData>(initialForm);
	const initialFormRef = useRef(initialForm);
	const [isLoading, setIsLoading] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});

	const { mutateAsync: createFertilizer } = usePostFertilizer();
	const { mutateAsync: updateFertilizer } = useUpdateFertilizer();

	const { mutateAsync: updateAvatarsFetilizer } = useUpdateAvatarsFetilizer();

	const set = (key: keyof FertilizerFormData, value: unknown) =>
		setForm((f) => ({ ...f, [key]: value }));

	const isDirty = () => JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

	const handleSubmit = async () => {
		const result = FertilizerSchema.safeParse(form);

		if (!result.success) {
			const fieldErrors: Record<string, string> = {};
			result.error.issues.forEach((issue) => {
				const path = String(issue.path[0] ?? '');
				if (path) fieldErrors[path] = issue.message;
			});
			setErrors(fieldErrors);
			const uniquePaths = [
				...new Set(result.error.issues.map((i) => String(i.path[0] ?? '')).filter(Boolean)),
			];
			const labels = uniquePaths.map((p) => FIELD_LABELS[p] ?? p).join(', ');
			toast.error(`Campos inválidos: ${labels}`);
			return;
		}

		setErrors({});
		const manufacturer = manufacturers.find((m) => m._id === form.manufacturerId);
		if (!manufacturer) {
			toast.error('Fabricante não encontrado');
			return;
		}

		setIsLoading(true);
		try {
			const showsCarbon = form.category === 'special' || form.category === 'organic';
			const payload = {
				manufacturer: manufacturer._id,
				name: form.name,
				category: form.category,
				applicationMethods: form.applicationMethods,
				nature: form.nature,
				active: form.active,
				note: form.note || undefined,
				N: parseNutrient(form.N),
				P2O5: parseNutrient(form.P2O5),
				K2O: parseNutrient(form.K2O),
				Ca: parseNutrient(form.Ca),
				Mg: parseNutrient(form.Mg),
				S: parseNutrient(form.S),
				C: showsCarbon ? parseNutrient(form.C) : null,
				B: parseNutrient(form.B),
				Cu: parseNutrient(form.Cu),
				Fe: parseNutrient(form.Fe),
				Mn: parseNutrient(form.Mn),
				Zn: parseNutrient(form.Zn),
				Mo: parseNutrient(form.Mo),
				CaO: parseNutrient(form.CaO),
				MgO: parseNutrient(form.MgO),
				PRNT: parseNutrient(form.PRNT),
			};
			if (id) {
				await updateFertilizer({ id, data: payload });

				try {
					if (file.length) {
						await updateAvatarsFetilizer({ id, file });
					}
				} catch (error) {
					console.error(error)
					toast.error('Erro ao atualizar avatar do fertilizante');
				}
				toast.success('Fertilizante atualizado com sucesso!');
			} else {
				await createFertilizer({
					...payload,
					...(file.length ? { file } : {}),
				});
				toast.success('Fertilizante salvo com sucesso!');
			}
			onSubmit();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Erro ao salvar fertilizante');
		} finally {
			setIsLoading(false);
		}
	};

	return { form, errors, isLoading, set, handleSubmit, isDirty };
};
